import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { AlertCircle } from 'lucide-react';

interface LandlordRouteProps {
  children: React.ReactNode;
  fallbackPath?: string;
}

export default function LandlordRoute({ children, fallbackPath = "/" }: LandlordRouteProps) {
  const { user, profile, loading, isHydrated, isAdmin } = useAuth();
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!isHydrated) setTimedOut(true);
    }, 5000);
    return () => clearTimeout(timeout);
  }, [isHydrated]);

  const authReady = isHydrated;

  if (!authReady && !timedOut) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (timedOut && !authReady) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4 max-w-md px-4">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
          <h2 className="text-xl font-semibold">Loading Timeout</h2>
          <p className="text-muted-foreground">
            Unable to verify your landlord access. Please check your connection and try again.
          </p>
          <Button onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Check if user is landlord or admin (isAdmin from backend user_roles)
  const isLandlord = profile?.user_type === 'landlord' || isAdmin;

  if (!isLandlord) {
    if (profile?.user_type === 'student') {
      return <Navigate to="/student-dashboard" replace />;
    }
    return <Navigate to={fallbackPath} replace />;
  }

  // Unverified landlords cannot access dashboard
  const emailConfirmed = (user as { email_confirmed_at?: string | null })?.email_confirmed_at;
  if (!emailConfirmed) {
    return <Navigate to="/auth" state={{ unverified: true }} replace />;
  }

  return <>{children}</>;
}
